import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cx } from '@/styled-system/css';
import StarRating from 'app/shared/core/StarRating';
import Button from 'app/shared/core/Button';
import ReviewActions from 'app/shared/flux/actions/ReviewActions';
import type { RootReduxState } from 'app/shared/flux/reducers';
import {
  buttonRow,
  characterCount,
  characterCountOver,
  commentTextarea,
  errorMessage,
  fieldLabel,
  formContainer,
  successMessage,
  tag,
  tagsContainer,
  tagSelected,
} from './reviewForm.styles';

const DEFAULT_POSITIVE_TAGS = [
  'Responded quickly',
  'Very helpful',
  'Great communication',
  'Professional',
  'Flexible scheduling',
];

const DEFAULT_NEGATIVE_TAGS = [
  'Did not respond',
  'Slow response',
  'Unhelpful',
  'Hard to reach',
  'Listing was inaccurate',
];

type ReviewFormProps = {
  listingAlias: string;
  onSubmitSuccess?: () => void;
  showSkip?: boolean;
  onSkip?: () => void;
  positiveTags?: Array<string>;
  negativeTags?: Array<string>;
  reviewType?: string;
};

export default function ReviewForm({
  listingAlias,
  onSubmitSuccess,
  showSkip = false,
  onSkip,
  positiveTags = DEFAULT_POSITIVE_TAGS,
  negativeTags = DEFAULT_NEGATIVE_TAGS,
  reviewType,
}: ReviewFormProps) {
  const dispatch = useDispatch();
  const { isSubmitting, isSubmitted, error } = useSelector((state: RootReduxState) => state.review);

  const [rating, setRating] = useState(0);
  const [selectedTags, setSelectedTags] = useState<Array<string>>([]);
  const [comment, setComment] = useState('');
  const [validationError, setValidationError] = useState('');
  const MAX_COMMENT_LENGTH = 255;

  useEffect(() => {
    if (isSubmitted && onSubmitSuccess) {
      const timeout = setTimeout(onSubmitSuccess, 1000);
      return () => clearTimeout(timeout);
    }
  }, [isSubmitted, onSubmitSuccess]);

  // Tags and custom comments are mutually exclusive — selecting a tag clears any typed comment,
  // and typing a comment clears selected tags (see onChange below).
  const toggleTag = (label: string) => {
    setSelectedTags((prev) => {
      if (prev.includes(label)) {
        return prev.filter((t) => t !== label);
      }
      const next = [...prev, label];
      if (next.join(', ').length > MAX_COMMENT_LENGTH) {
        return prev;
      }
      return next;
    });
    setComment('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    if (rating === 0) {
      setValidationError('Please select a rating.');
      return;
    }

    const fullComment = comment || selectedTags.join(', ');

    if (fullComment.length > MAX_COMMENT_LENGTH) {
      setValidationError(`Comment cannot exceed ${MAX_COMMENT_LENGTH} characters.`);
      return;
    }

    dispatch(ReviewActions.submitReview({ listingAlias, rating, comment: fullComment, reviewType }));
  };

  const effectiveComment = comment || selectedTags.join(', ');
  const suggestedTags = rating >= 3 ? positiveTags : negativeTags;

  if (isSubmitted) {
    return <div className={successMessage}>Thank you for your review!</div>;
  }

  return (
    <form onSubmit={handleSubmit} noValidate className={formContainer}>
      <div>
        <div className={fieldLabel}>Your Rating</div>
        <StarRating
          value={rating}
          onChange={(val) => {
            setRating(val);
            setSelectedTags([]);
          }}
          disabled={isSubmitting}
        />
        {validationError && <div className={errorMessage}>{validationError}</div>}
      </div>

      {rating > 0 && (
        <div>
          <div className={fieldLabel}>How was your experience?</div>
          <div className={tagsContainer}>
            {suggestedTags.map((label) => (
              <button
                key={label}
                type="button"
                className={cx(tag, selectedTags.includes(label) && tagSelected)}
                onClick={() => toggleTag(label)}
                disabled={isSubmitting}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <label htmlFor="review-comment" className={fieldLabel}>
          Comments
        </label>
        <textarea
          id="review-comment"
          aria-label="Review comment"
          className={commentTextarea}
          placeholder="Share your experience with this property..."
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
            if (e.target.value) setSelectedTags([]);
          }}
          disabled={isSubmitting}
          maxLength={MAX_COMMENT_LENGTH}
        />
        <div className={cx(characterCount, effectiveComment.length >= MAX_COMMENT_LENGTH && characterCountOver)}>
          {effectiveComment.length}/{MAX_COMMENT_LENGTH}
        </div>
      </div>

      {error && <div className={errorMessage}>{error}</div>}

      <div className={buttonRow}>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit Review'}
        </Button>
        {showSkip && onSkip && (
          <Button type="button" btnType="secondary" onClick={onSkip} disabled={isSubmitting}>
            Skip
          </Button>
        )}
      </div>
    </form>
  );
}
